<template>
	<div class="embedded-lead-form">
		<LoadingIndicator v-if="isLoading"></LoadingIndicator>

		<form v-else-if="!isLoading" @submit="onSubmit">
			<div class="row mb-6">
				<div class="col-md-6 mb-6 mb-md-0">
					<div class="form-floating">
						<input
							id="firstName"
							v-model="firstNameValue"
							name="firstName"
							type="text"
							maxlength="50"
							class="form-control"
							:class="firstNameError ? 'is-invalid' : ''"
							placeholder="First Name"
							data-testid="first-name"
							@blur="firstNameBlur($event, true)"
						/>
						<label for="firstName">First Name&ast;</label>
						<div class="invalid-feedback mt-1" data-testid="first-name-error">
							{{ firstNameError }}
						</div>
					</div>
				</div>
				<div class="col-md-6">
					<div class="form-floating">
						<input
							id="lastName"
							v-model="lastNameValue"
							name="lastName"
							type="text"
							maxlength="50"
							class="form-control"
							:class="lastNameError ? 'is-invalid' : ''"
							placeholder="Last Name"
							data-testid="last-name"
							@blur="lastNameBlur($event, true)"
						/>
						<label for="lastName">Last Name&ast;</label>
						<div class="invalid-feedback mt-1" data-testid="last-name-error">
							{{ lastNameError }}
						</div>
					</div>
				</div>
			</div>
			<div class="row mb-6">
				<div class="col">
					<div class="form-floating">
						<input
							id="phone"
							v-model="phoneValue"
							v-maska="phoneMaskaOptions"
							name="telephone2"
							type="text"
							class="form-control"
							:class="phoneMeta.touched && phoneError ? 'is-invalid' : ''"
							placeholder="Phone"
							data-testid="phone"
							@blur="phoneBlur($event, true)"
						/>
						<label for="phone">Phone&ast;</label>
						<div class="invalid-feedback mt-1" data-testid="phone-error">
							{{ phoneError }}
						</div>
					</div>
				</div>
			</div>
			<div class="row mb-6">
				<div class="col">
					<div class="form-floating">
						<input
							id="email"
							v-model="emailValue"
							name="emailAddress"
							type="text"
							maxlength="140"
							class="form-control"
							:class="emailMeta.touched && emailError ? 'is-invalid' : ''"
							placeholder="email"
							data-testid="email"
							@blur="emailBlur($event, true)"
						/>
						<label for="email">Email&ast;</label>
						<div class="invalid-feedback mt-1" data-testid="email-error">
							{{ emailError }}
						</div>
					</div>
				</div>
			</div>

			<div class="row mb-6">
				<div class="col">
					<div class="form-check ps-0">
						<input
							id="tou"
							v-model="touValue"
							name="tou"
							class="form-check-input ms-0"
							:class="touError ? 'is-invalid' : ''"
							:value="false"
							type="checkbox"
							data-testid="tou"
						/>
						<label class="form-check-label small text-opacity-75 d-block ps-3" for="tou">
							{{ consentText }}
						</label>
						<div class="invalid-feedback mt-1" data-testid="tou-error">{{ touError }}</div>
					</div>
				</div>
				<div class="ps-3 mt-1">
					<a
						href="https://my.lifetime.life/policy/terms-of-use.html"
						target="_blank"
						rel="noopener noreferrer"
						type="button"
						class="link-underlined small text-nowrap me-1 btn-primary"
					>
						Terms of Use
					</a>
					<a
						href="https://my.lifetime.life/policy/privacy-policy.html "
						target="_blank"
						rel="noopener noreferrer"
						type="button"
						class="link-underlined small text-nowrap btn-primary"
					>
						Privacy Policy
					</a>
				</div>
			</div>
			<div class="d-grid gap-2">
				<button
					v-adobe-data-layer="joinADLConfig"
					class="btn btn-dark"
					data-testid="submit-btn"
					type="submit"
				>
					{{ submitBtnText }}
				</button>
			</div>
			<input name="email-template" type="hidden" :value="emailTemplate" />
		</form>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, onBeforeMount, type Ref } from 'vue';
import { useField, useForm } from 'vee-validate';
import { vMaska } from 'maska/vue';
import { useMaska } from '@/composables/use-maska';
import { validateEmail, validateName, validatePhone } from '@/util/validators';
import LoadingIndicator from 'Lib/components/shared/LoadingIndicator.vue';
import { leadFormService } from '@/services/leadFormService';
import { getAuthoredContent } from '@/util/getAuthoredContent';
import { club } from '@/util/club';
import { logError } from '@/util/logger';
import { prerenderGateCheck } from '@/util/prerenderGateCheck';
import type {
	EmbeddedLeadFormAuthoredContent,
	IFormData
} from 'Types/aemui/embedded-lead-form/aemuiEmbeddedLeadForm';
import vAdobeDataLayer from '@/directives/AdobeDataLayer';
import { getUtmParams } from '@/util/utm';

import type { ltFramework } from '@life-time-inc/enterprise-framework-core';

const clubData: Ref<ltFramework.IClubDetailsFromDOM> = ref(club);
const isLoading = ref<boolean>(true);
const authoredContent: Ref<EmbeddedLeadFormAuthoredContent> = ref({});

const emailTemplate = computed(() => {
	return authoredContent.value.emailTemplate ?? '12305_11756';
});

const confirmationUrl = computed(() => {
	return authoredContent.value.confirmationUrl ?? '/join/request-info/confirmation.html';
});

const errorUrl = computed(() => {
	return authoredContent.value.errorUrl ?? '/error/lead.html';
});

const submitBtnText = computed(() => {
	return authoredContent.value.submitBtnText ?? 'Submit';
});

const inquiryType = computed(() => {
	return authoredContent.value.inquiryType ?? 'Club Info Request';
});

onBeforeMount(async () => {
	if (prerenderGateCheck()) {
		return;
	}

	authoredContent.value = getAuthoredContent<EmbeddedLeadFormAuthoredContent>(
		'aemuiEmbeddedLeadFormScript'
	);

	isLoading.value = false;
});

const consentText =
	"I acknowledge that I am at least 18 years of age and agree to the Privacy Policy and Terms of Use and expressly consent to Life Time's use of this information to contact me, which may include sending me electronic commercial or promotional messages, which I may opt out of at any time.";

const { handleSubmit } = useForm<IFormData>({
	initialValues: {
		addressLatitude: '',
		addressLongitude: '',
		birthYear: '',
		channel: 'AEM',
		clubStatus: '',
		clubType: 'club',
		communicationConsent: '',
		consentDateAndTime: '',
		consentIPAddress: '',
		consentText: consentText,
		currentMember: '',
		dcmp: '',
		description: '',
		device: '',
		emailAddress: '',
		emailTemplate: emailTemplate.value,
		firstName: '',
		gcId: '',
		gcLId: '',
		group: '',
		inquiryType: inquiryType.value, // Maps to LeadSource in CRM
		interests: '',
		keyWords: '',
		landingPage: `${document.URL}`,
		lastName: '',
		leadType: 'Web',
		membershipInfoRequested: 'Wait List',
		mmsClubId: '',
		operatingSystem: '',
		referringDomain: `${document.location.origin}`,
		referringPage: `${document.location.pathname}`,
		subject: '',
		telephone2: '',
		tou: false,
		visitCount: '',
		visitorId: ''
	}
});

const onSubmit = handleSubmit(async (values) => {
	values = {
		...values,
		...getUtmParams(),
		consentDateAndTime: document.lastModified,
		emailTemplate: emailTemplate.value,
		mmsClubId: clubData.value.mmsClubId.toString(),
		inquiryType: inquiryType.value
	};

	try {
		isLoading.value = true;
		await leadFormService.submitForm(values);
		window.location.assign(confirmationUrl.value);
	} catch (error: unknown) {
		logError('Embedded Lead Form submit error', { values }, error);
		window.location.assign(errorUrl.value);
	} finally {
		isLoading.value = false;
	}
});

const {
	errorMessage: firstNameError,
	handleBlur: firstNameBlur,
	value: firstNameValue
} = useField<string>('firstName', (value: string) =>
	validateName(value, 'Please enter your first name')
);

const {
	errorMessage: lastNameError,
	handleBlur: lastNameBlur,
	value: lastNameValue
} = useField<string>('lastName', (value: string) =>
	validateName(value, 'Please enter your last name')
);

const { phoneMaskaOptions } = useMaska();
const {
	errorMessage: phoneError,
	handleBlur: phoneBlur,
	value: phoneValue,
	meta: phoneMeta
} = useField<string>('telephone2', (value: string) =>
	validatePhone(value, 'Please enter your phone number')
);

const {
	errorMessage: emailError,
	handleBlur: emailBlur,
	value: emailValue,
	meta: emailMeta
} = useField<string>('emailAddress', (value: string) =>
	validateEmail(value, 'Please enter an email address')
);

const { errorMessage: touError, value: touValue } = useField<boolean>('tou', (value: boolean) =>
	value ? true : 'Please accept the terms'
);

const joinADLConfig = {
	name: 'embeddedleadform',
	type: 'button',
	title: 'Join the waitlist',
	link: confirmationUrl.value
};

defineExpose({ clubData, isLoading });
</script>
